import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import type { Uuid } from '@/api/types';
import { useAuth } from './auth-context';

const ID_KEY = 'tlp.admin.pharmacyScope';
const NAME_KEY = 'tlp.admin.pharmacyScopeName';

type PharmacyScopeValue = {
  /** Null until a pharmacy is chosen — every stock and listing route needs one. */
  pharmacyId: Uuid | null;
  /**
   * Cached display name, so the header does not show a bare UUID for the frame
   * before the pharmacy list loads — or at all, if the current search has
   * scrolled the scoped pharmacy out of the results.
   */
  pharmacyName: string | null;
  setPharmacy: (id: Uuid | null, name?: string | null) => void;
  /** Ids the caller is staff at, straight from the access token via /auth/me. */
  assigned: Uuid[];
};

const PharmacyScopeContext = createContext<PharmacyScopeValue | null>(null);

/**
 * Which pharmacy the stock and listing screens are looking at.
 *
 * Held here rather than in the URL because it persists across navigation: an
 * admin moves between balances, batches and listings for one shop, and putting
 * it in the path would mean every link had to carry it.
 */
export function PharmacyScopeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [pharmacyId, setId] = useState<Uuid | null>(() => localStorage.getItem(ID_KEY));
  const [pharmacyName, setName] = useState<string | null>(() => localStorage.getItem(NAME_KEY));

  const assigned = useMemo(() => user?.pharmacyIds ?? [], [user?.pharmacyIds]);

  const setPharmacy = useCallback((id: Uuid | null, name: string | null = null) => {
    setId(id);
    setName(name);

    if (id === null) {
      localStorage.removeItem(ID_KEY);
      localStorage.removeItem(NAME_KEY);
      return;
    }

    localStorage.setItem(ID_KEY, id);
    if (name) localStorage.setItem(NAME_KEY, name);
    else localStorage.removeItem(NAME_KEY);
  }, []);

  // Staff have exactly one pharmacy far more often than not; making them pick it
  // on every sign-in would be ceremony for no decision.
  useEffect(() => {
    if (pharmacyId === null && assigned.length === 1 && assigned[0]) {
      setPharmacy(assigned[0]);
    }
  }, [assigned, pharmacyId, setPharmacy]);

  const value = useMemo<PharmacyScopeValue>(
    () => ({ pharmacyId, pharmacyName, setPharmacy, assigned }),
    [pharmacyId, pharmacyName, setPharmacy, assigned],
  );

  return <PharmacyScopeContext value={value}>{children}</PharmacyScopeContext>;
}

export function usePharmacyScope(): PharmacyScopeValue {
  const context = use(PharmacyScopeContext);
  if (!context) throw new Error('usePharmacyScope must be used inside <PharmacyScopeProvider>');
  return context;
}
